"use client";

import React from "react";
import { Calendar, Fuel, Settings, ChevronRight, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
const wp_query_params = {
  per_page: 6,
  product_cat: [16],
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function UsedCarGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border shadow-sm">
            <AspectRatio ratio={16 / 9} className="bg-muted animate-pulse" />
            <CardContent className="p-6 space-y-4">
              <div className="h-6 bg-muted animate-pulse rounded w-3/4" />
              <div className="flex gap-2">
                <div className="h-5 bg-muted animate-pulse rounded w-16" />
                <div className="h-5 bg-muted animate-pulse rounded w-16" />
                <div className="h-5 bg-muted animate-pulse rounded w-16" />
              </div>
              <div className="h-8 bg-muted animate-pulse rounded w-1/3 mt-4" />
            </CardContent>
            <CardFooter className="p-6 pt-0">
              <div className="h-10 bg-muted animate-pulse rounded w-full" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center bg-muted/30 rounded-md border border-border border-dashed">
        <CircleAlert className="w-10 h-10 text-muted-foreground mb-4" />
        <p className="text-muted-foreground font-medium">Unable to load marine parts. Please try again later.</p>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center py-16 text-center bg-muted/30 rounded-md border border-border border-dashed">
        <p className="text-muted-foreground font-medium text-lg">No featured parts available at the moment.</p>
        <p className="text-sm text-muted-foreground mt-2">Check back soon for our latest OEM compatible stock.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {products.map((product, i) => {
      const sku = product.sku || "ALT-99283";
      const compatibility = "Yamaha Compatible";
      const condition = "OEM Quality";
      return <Card key={product.id} data-index={i} className="overflow-hidden border-border shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 flex flex-col bg-card">
            <Link to={product.link} className="block relative group">
              <AspectRatio ratio={16 / 9} className="bg-muted overflow-hidden">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center bg-secondary text-secondary-foreground">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686328/img/marine_part_placeholder.png" alt="Marine Part" className="w-1/2 opacity-20" />
                  </div>}
                <div className="absolute top-2 right-2">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686328/img/qr_code_label.png" alt="Authenticity QR" className="w-12 h-12 bg-white p-1 rounded-sm shadow-sm" />
                </div>
              </AspectRatio>
            </Link>

            <CardHeader className="p-6 pb-4 flex-none">
              <Link to={product.link}>
                <CardTitle className="font-default text-xl text-foreground line-clamp-1 hover:text-primary transition-colors">
                  {product.title}
                </CardTitle>
              </Link>
              <div className="font-mono text-xs text-muted-foreground tracking-wider mt-1">
                SKU: {sku}
              </div>
            </CardHeader>

            <CardContent className="p-6 pt-0 flex-grow flex flex-col justify-between space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="bg-primary text-primary-foreground font-default text-[10px] uppercase tracking-widest rounded-md">
                  {compatibility}
                </Badge>
                <Badge variant="secondary" className="bg-secondary/30 text-foreground font-default text-[10px] uppercase tracking-widest rounded-md">
                  {condition}
                </Badge>
              </div>

              <div className="pt-2">
                <span className="text-[10px] text-muted-foreground font-mono uppercase block mb-1">Unit Price (Excl. VAT)</span>
                <div className="font-default text-2xl font-bold text-foreground">
                  ${product.price?.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
                </div>
              </div>
            </CardContent>

            <CardFooter className="p-6 pt-0 flex-none">
              <AddToCartButton productId={product.id} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold rounded-md transition-colors" text="Request Quotation" showQuantity={false} />
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function ProductListing() {
  return <section data-section-id="235972" id="product-listing" className="py-20 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="font-default text-3xl md:text-4xl font-bold text-primary mb-4 tracking-tight">
              Featured Marine Components
            </h2>
            <p className="text-lg text-muted-foreground font-default">
              Engineered for reliability and trusted marine performance. Explore our selection of premium spare parts, fully compatible with Yamaha outboards and built to Japanese OEM standards.
            </p>
          </div>
          <div className="flex-shrink-0">
            <Button variant="outline" asChild className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md transition-all">
              <Link to="/ProductsPage">
                View Full Catalog <ChevronRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 6,
        product_cat: [16],
        order: "desc",
        orderby: "date"
      }}>
          <UsedCarGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}